import { gql } from '@apollo/client';

import { ResponsiveImageFragment } from 'approot/shared/graphql/responsive-images/responsive-images.graphql';
import { interactiveContentStyleBackgroundFragment } from './styles/styles.graphql';
import { DictionaryBookItemFragment } from 'approot/dictionary/dictionary.graphql';
import { SuggestedAnswerFragment } from './components/suggested-answers/suggested-answers.graphql';

const InteractiveSubActivityFragment = gql`
  fragment InteractiveSubActivityFragment on SubActivityComponentProperties {
    activityComponentId
    subActivitySort
    subActivityLabel
    instructions
    areInstructionsVisible
    previews {
      format
      url
    }
  }
`;

const InteractiveInfoBoxFragment = gql`
  fragment InteractiveInfoBoxFragment on ActivitiesInfoBoxComponent {
    id
    language
    type
    properties {
      richtext
      style {
        arrowPosition
        arrowAlignment
        baseColour
        backgroundColour
        border
        width
        textAlignment
      }
    }
    parentProperties {
      columnId
    }
    sort
    sectionId
  }
`;

export const InteractiveResponseFragment = gql`
  fragment InteractiveResponseFragment on ActivitiesResponseComponent {
    id
    type
    language
    properties {
      textResponse {
        fields {
          itemId
          placeholder
          numberOfLines
          sort
          isPlaceholderScaffold
          baseColour
        }
      }
    }
    subActivityProperties {
      ...InteractiveSubActivityFragment
    }
    suggestedAnswerProperties {
      ...SuggestedAnswerFragment
    }
    sectionId
    sort
  }
  ${InteractiveSubActivityFragment}
  ${SuggestedAnswerFragment}
`;

const InteractiveVideoFragment = gql`
  fragment InteractiveVideoFragment on ActivitiesVideoComponent {
    id
    type
    language
    properties {
      stimulusId
      showCaptionOnLesson
      dimensionData {
        width
        height
      }
    }
    stimulusResourceProperties {
      coverImage {
        original
      }
      attributions {
        author
        copyright
        licence
        modifications
        modificationDescription
        contentWarning
      }
      videoData {
        link
        embed
      }
      xlCoverImage {
        ...ResponsiveImageFragment
        original
      }
      xlCoverImageUrls {
        width
        height
        urls {
          url
          quality
        }
      }
      link
      resourceName
      description
    }
    parentProperties {
      columnId
    }
    sectionId
    sort
  }
`;

const InteractiveAudioFragment = gql`
  fragment InteractiveAudioFragment on ActivitiesAudioComponent {
    id
    type
    language
    properties {
      stimulusId
      showCaptionOnLesson
      dimensionData {
        width
        height
      }
    }
    stimulusResourceProperties {
      coverImage {
        original
      }
      attributions {
        author
        copyright
        licence
        modifications
        modificationDescription
        contentWarning
      }
      videoData {
        link
        embed
      }
      xlCoverImage {
        ...ResponsiveImageFragment
        original
      }
      xlCoverImageUrls {
        width
        height
        urls {
          url
          quality
        }
      }
      link
      resourceName
      description
    }
    parentProperties {
      columnId
    }
    sectionId
    sort
  }
`;

const InteractiveImageFragment = gql`
  fragment InteractiveImageFragment on ActivitiesImageComponent {
    id
    type
    language
    properties {
      stimulusId
      showCaptionOnLesson
      caption
      altText
      sourceUrl
      dimensionData {
        width
        height
      }
      cropData {
        x
        y
        width
        height
        zoom
        naturalWidth
        naturalHeight
      }
      imageUrls {
        width
        height
        urls {
          url
          quality
        }
      }
      image {
        original
        fileName
        key
        data {
          designId
          flip
        }
      }
    }
    stimulusResourceProperties {
      image {
        key
        fileName
        original
        responsive {
          width
          height
          urls {
            url
            quality
          }
        }
      }
      link
      imageResourceName: resourceName
      imageDescription: description
      altText
      coverImage {
        key
        fileName
        original
        responsive {
          width
          height
          urls {
            url
            quality
          }
        }
      }
    }
    parentProperties {
      columnId
    }
    sectionId
    sort
  }
  ${ResponsiveImageFragment}
`;

const InteractiveImageSphereFragment = gql`
  fragment InteractiveImageSphereFragment on ActivitiesImageSphereComponent {
    id
    type
    language
    properties {
      showCaptionOnLesson
      caption
      altText
      sourceUrl
      dimensionData {
        width
        height
      }
      image {
        original
        fileName
        key
        data {
          flip
        }
      }
      imageUrls {
        width
        height
        urls {
          url
          quality
        }
      }
      printImage {
        original
        fileName
        key
        alt
      }
      printImageUrls {
        width
        height
        urls {
          url
          quality
        }
      }
    }
    parentProperties {
      columnId
    }
    sectionId
    sort
  }
`;

const InteractiveLinkFragment = gql`
  fragment InteractiveLinkFragment on ActivitiesLinkComponent {
    id
    type
    language
    properties {
      stimulusId
      layout {
        display
      }
    }
    stimulusResourceProperties {
      link
      resourceName
      description
      coverImage {
        ...ResponsiveImageFragment
      }
    }
    parentProperties {
      columnId
    }
    sectionId
    sort
  }
  ${ResponsiveImageFragment}
`;

const InteractiveWebToolFragment = gql`
  fragment InteractiveWebToolFragment on ActivitiesWebToolComponent {
    id
    type
    language
    properties {
      stimulusId
      layout {
        display
      }
    }
    stimulusResourceProperties {
      link
      resourceName
      description
      coverImage {
        ...ResponsiveImageFragment
      }
    }
    parentProperties {
      columnId
    }
    sectionId
    sort
  }
  ${ResponsiveImageFragment}
`;

const InteractiveGuidedResearchFragment = gql`
  fragment InteractiveGuidedResearchFragment on ActivitiesGuidedResearchComponent {
    id
    type
    language
    properties {
      stimulusId
      layout {
        display
      }
    }
    stimulusResourceProperties {
      links {
        link
        title
        description
      }
      link
      slug
      coverImage {
        ...ResponsiveImageFragment
      }
      resourceName
      description
    }
    parentProperties {
      columnId
    }
    sectionId
    sort
  }
  ${ResponsiveImageFragment}
`;

const InteractiveSlideshowFragment = gql`
  fragment InteractiveSlideshowFragment on ActivitiesSlideshowComponent {
    id
    type
    language
    sort
    slideshowImages {
      ...InteractiveImageFragment
    }
    properties {
      dimensionData {
        width
        height
      }
      showSlideCaptionOnLesson: showCaptionOnLesson
    }
    sectionId
  }
  ${InteractiveImageFragment}
`;

export const InteractiveDocumentFragment = gql`
  fragment InteractiveDocumentFragment on ActivitiesDocumentComponent {
    id
    type
    language
    properties {
      stimulusId
      showCaptionOnLesson
      dimensionData {
        width
        height
      }
    }
    stimulusResourceProperties {
      resourceName
      description
      link
      fileName
      documentKey
      coverImage {
        ...ResponsiveImageFragment
      }
      xlCoverImage {
        ...ResponsiveImageFragment
        original
      }
      xlCoverImageUrls {
        width
        height
        urls {
          url
          quality
        }
      }
    }
    sectionId
    sort
  }
`;

export const InteractiveEbookFragment = gql`
  fragment InteractiveEbookFragment on ActivitiesEbookComponent {
    id
    type
    language
    properties {
      stimulusId
      showCaptionOnLesson
      dimensionData {
        width
        height
      }
    }
    stimulusResourceProperties {
      resourceName
      description
      link
      fileName
      documentKey
      coverImage {
        ...ResponsiveImageFragment
      }
      xlCoverImage {
        ...ResponsiveImageFragment
        original
      }
      xlCoverImageUrls {
        width
        height
        urls {
          url
          quality
        }
      }
    }
    sectionId
    sort
  }
`;

export const InteractiveEbookInteractiveFragment = gql`
  fragment InteractiveEbookInteractiveFragment on ActivitiesInteractiveEbookComponent {
    id
    type
    language
    properties {
      stimulusId
      showCaptionOnLesson
      dimensionData {
        width
        height
      }
    }
    stimulusResourceProperties {
      link
      slug
      resourceName
      description
      coverImage {
        ...ResponsiveImageFragment
      }
      xlCoverImage {
        ...ResponsiveImageFragment
        original
      }
      xlCoverImageUrls {
        width
        height
        urls {
          url
          quality
        }
      }
    }
    sectionId
    sort
  }
`;

export const InteractiveInteractiveFragment = gql`
  fragment InteractiveInteractiveFragment on ActivitiesInteractiveComponent {
    id
    type
    language
    properties {
      stimulusId
      showCaptionOnLesson
      dimensionData {
        width
        height
      }
    }
    stimulusResourceProperties {
      link
      slug
      resourceName
      description
      coverImage {
        ...ResponsiveImageFragment
      }
      xlCoverImage {
        ...ResponsiveImageFragment
        original
      }
      xlCoverImageUrls {
        width
        height
        urls {
          url
          quality
        }
      }
    }
    sectionId
    sort
  }
`;

export const InteractiveMultipleChoiceFragment = gql`
  fragment InteractiveMultipleChoiceFragment on ActivitiesMultipleChoiceComponent {
    id
    type
    language
    properties {
      layout {
        display
      }
      multipleChoice {
        variant
        revealAnswer
        optionMinHeight
        rowsGridLayout
        showOptionLetter
        imagePosition
        answerMode
        isFreeAnswerMultiSelect
        options {
          itemId
          sort
          correctAnswerBoolean
          text
          sourceUrl
          image {
            itemId
            key
            fileName
            alt
            original
            data {
              flip
            }
          }
          imageUrls {
            width
            height
            urls {
              url
              quality
            }
          }
          imageLayout
          cropData {
            x
            y
            width
            height
            zoom
            naturalWidth
            naturalHeight
          }
        }
        moreInformation
      }
    }
    sort
    subActivityProperties {
      ...InteractiveSubActivityFragment
    }
    suggestedAnswerProperties {
      ...SuggestedAnswerFragment
    }
    sectionId
  }
  ${InteractiveSubActivityFragment}
  ${SuggestedAnswerFragment}
`;

export const InteractiveEquationResponseFragment = gql`
  fragment InteractiveEquationResponseFragment on ActivitiesEquationResponseComponent {
    id
    type
    language
    properties {
      revealAnswer
      operation
      layout {
        orientation
      }
      moreInformation
      firstOperand
      secondOperand
    }
    subActivityProperties {
      ...InteractiveSubActivityFragment
    }
    sectionId
    sort
  }
  ${InteractiveSubActivityFragment}
`;

const InteractiveVocabularyFragment = gql`
  fragment InteractiveVocabularyFragment on ActivitiesVocabularyComponent {
    id
    type
    language
    properties {
      definitionIds
    }
    parentProperties {
      columnId
    }
    vocabularyDefinitions {
      ...DictionaryBookItemFragment
    }
    sectionId
    sort
  }
  ${DictionaryBookItemFragment}
`;

const InteractiveFillInBlanksResponseFragment = gql`
  fragment InteractiveFillInBlanksResponseFragment on ActivitiesFillInBlanksResponseComponent {
    id
    type
    language
    sort
    properties {
      fillInBlanks {
        fontFamily
        sentences {
          itemId
          sort
          textBlocks {
            itemId
            sort
            text
          }
          answerBlocks {
            itemId
            sort
            width
            colour
            showLabel
            label
            showPlaceholderAsScaffold
            placeholder
            punctuation
          }
        }
      }
    }
    subActivityProperties {
      ...InteractiveSubActivityFragment
    }
    suggestedAnswerProperties {
      ...SuggestedAnswerFragment
    }
    sectionId
  }
  ${InteractiveSubActivityFragment}
  ${SuggestedAnswerFragment}
`;

const InteractiveDrawFragment = gql`
  fragment InteractiveDrawFragment on ActivitiesDrawComponent {
    id
    type
    language
    sort
    properties {
      stimulusId
      draw {
        response
      }
      dimensionData {
        width
        height
      }
      image {
        ...ResponsiveImageFragment
        imageType
        classification
        data {
          flip
        }
      }
      altText
      sourceUrl
    }
    subActivityProperties {
      ...InteractiveSubActivityFragment
    }
    suggestedAnswerProperties {
      ...SuggestedAnswerFragment
    }
    sectionId
  }
  ${InteractiveSubActivityFragment}
  ${SuggestedAnswerFragment}
`;

const InteractiveDiscussionFragment = gql`
  fragment InteractiveDiscussionFragment on ActivitiesDiscussionComponent {
    id
    type
    language
    sort
    properties {
      style {
        arrowPosition
        arrowAlignment
        baseColour
        backgroundColour
        border
        width
        textAlignment
      }
      discussion {
        responseTypes
        instruction
      }
    }
    subActivityProperties {
      ...InteractiveSubActivityFragment
    }
    suggestedAnswerProperties {
      ...SuggestedAnswerFragment
    }
    sectionId
  }
  ${InteractiveSubActivityFragment}
  ${SuggestedAnswerFragment}
`;

const InteractiveTableFragment = gql`
  fragment InteractiveTableFragment on ActivitiesTableComponent {
    id
    type
    language
    sort
    properties {
      table {
        isReadOnly
        showColumnHeaders
        showRowHeaders
        columnWidths
        rows {
          cells {
            value
          }
        }
      }
    }
    subActivityProperties {
      ...InteractiveSubActivityFragment
    }
    suggestedAnswerProperties {
      ...SuggestedAnswerFragment
    }
    sectionId
  }
  ${InteractiveSubActivityFragment}
  ${SuggestedAnswerFragment}
`;

export const InteractiveInteractiveResponseSequenceFragment = gql`
  fragment InteractiveInteractiveResponseFragment on ActivitiesInteractiveResponseComponent {
    id
    type
    language
    sort
    properties {
      interactiveResponse {
        title
        subTitle
        howToPlay
        moreInformation
        labelLength
        labelBorder
        checkAnswerAtEnd
        variant
        items {
          itemId
          questionLabel
          answerLabel
          sort
          sourceUrl
          imageMode
          text
          richtext
          baseColour
          cropData {
            x
            y
            width
            height
            zoom
            naturalWidth
            naturalHeight
          }
          image {
            itemId
            key
            fileName
            description
            original
            data {
              flip
            }
          }
          audio {
            key
            fileName
            original
          }
          imageUrls {
            width
            height
            urls {
              url
              quality
            }
          }
        }
      }
      responseDimensionData: dimensionData {
        width
        height
      }
      responseStyle: style {
        backgroundImage {
          ...ResponsiveImageFragment
        }
        baseColour
        backgroundMatchActivity
      }
    }
    subActivityProperties {
      ...InteractiveSubActivityFragment
    }
    suggestedAnswerProperties {
      ...SuggestedAnswerFragment
    }
    sectionId
  }
  ${InteractiveSubActivityFragment}
  ${SuggestedAnswerFragment}
`;

export const InteractiveInteractiveResponseCategoriseFragment = gql`
  fragment InteractiveInteractiveResponseCategoriseFragment on ActivitiesCategoriseResponseComponent {
    id
    type
    language
    sort
    properties {
      interactiveResponseCategorise {
        title
        subTitle
        howToPlay
        moreInformation
        checkAnswerAtEnd
        showNumberOfItemsHint
        answerType
        variant
        items {
          itemId
          questionLabel
          answerLabel
          sort
          sourceUrl
          categoryId
          imageMode
          baseColour
          text
          richtext
          cropData {
            x
            y
            width
            height
            zoom
            naturalWidth
            naturalHeight
          }
          image {
            itemId
            key
            fileName
            description
            original
            data {
              flip
            }
          }
          audio {
            key
            fileName
            original
          }
          imageUrls {
            width
            height
            urls {
              url
              quality
            }
          }
        }
        categories {
          id
          label
        }
      }
      categoriseDimensionData: dimensionData {
        width
        height
      }
      categoriseStyle: style {
        backgroundImage {
          ...ResponsiveImageFragment
        }
        baseColour
        backgroundMatchActivity
      }
    }
    subActivityProperties {
      ...InteractiveSubActivityFragment
    }
    suggestedAnswerProperties {
      ...SuggestedAnswerFragment
    }
    sectionId
  }
  ${InteractiveSubActivityFragment}
  ${SuggestedAnswerFragment}
`;

const InteractiveRiveAnimationFragment = gql`
  fragment InteractiveRiveAnimationFragment on ActivitiesRiveAnimationComponent {
    id
    type
    language
    properties {
      sourceUrl
      rive {
        key
        fileName
        link
        enableResetButton
        enableTextLabels
        textLabels {
          itemId
          text
          sort
        }
        enableButtons
        buttons {
          itemId
          text
          sort
        }
        buttonsPosition
      }
      dimensionData {
        width
        height
      }
      imageUrls {
        width
        height
        urls {
          url
          quality
        }
      }
      image {
        original
        fileName
        key
        alt
        data {
          designId
        }
      }
    }
    parentProperties {
      columnId
    }
    sectionId
    sort
  }
`;

const InteractiveGifFragment = gql`
  fragment InteractiveGifFragment on ActivitiesGifComponent {
    id
    type
    language
    properties {
      sourceUrl
      dimensionData {
        width
        height
      }
      image {
        original
        fileName
        key
        alt
      }
      imageUrls {
        width
        height
        urls {
          url
          quality
        }
      }
      printImage {
        original
        fileName
        key
        alt
      }
      printImageUrls {
        width
        height
        urls {
          url
          quality
        }
      }
    }
    parentProperties {
      columnId
    }
    sectionId
    sort
  }
`;

export const InteractiveActivityFragment = gql`
  fragment InteractiveActivityFragment on ActivitiesActivityComponent {
    id
    type
    language
    properties {
      activitySettings {
        differentiation
        duration
        checkPoint
        furtherEnrichment
      }
      subActivitiesLabelType
    }
    sectionId
    sort
    subActivities {
      ...InteractiveResponseFragment
      ...InteractiveMultipleChoiceFragment
      ...InteractiveEquationResponseFragment
      ...InteractiveFillInBlanksResponseFragment
      ...InteractiveDrawFragment
      ...InteractiveTableFragment
      ...InteractiveDiscussionFragment
      ...InteractiveInteractiveResponseFragment
      ...InteractiveInteractiveResponseCategoriseFragment
    }
  }
  ${InteractiveResponseFragment}
  ${InteractiveMultipleChoiceFragment}
  ${InteractiveEquationResponseFragment}
  ${InteractiveFillInBlanksResponseFragment}
  ${InteractiveDrawFragment}
  ${InteractiveTableFragment}
  ${InteractiveDiscussionFragment}
  ${InteractiveInteractiveResponseSequenceFragment}
  ${InteractiveInteractiveResponseCategoriseFragment}
`;

export const InteractiveColumnsFragment = gql`
  fragment InteractiveColumnsFragment on ActivitiesColumnsComponent {
    id
    type
    language
    sort
    sectionId
    columnProperties: properties {
      style {
        backgroundColour
        border
        baseColour
      }
    }
    columns {
      language
      index
      columnId
      colspan
      alignment
      style {
        backgroundColour
        border
        baseColour
      }
      components {
        ...InteractiveInfoBoxFragment
        ...InteractiveVideoFragment
        ...InteractiveAudioFragment
        ...InteractiveResponseFragment
        ...InteractiveImageFragment
        ...InteractiveImageSphereFragment
        ...InteractiveLinkFragment
        ...InteractiveWebToolFragment
        ...InteractiveGuidedResearchFragment
        ...InteractiveEquationResponseFragment
        ...InteractiveSlideshowFragment
        ...InteractiveDocumentFragment
        ...InteractiveEbookFragment
        ...InteractiveEbookInteractiveFragment
        ...InteractiveInteractiveFragment
        ...InteractiveMultipleChoiceFragment
        ...InteractiveActivityFragment
        ...InteractiveVocabularyFragment
        ...InteractiveFillInBlanksResponseFragment
        ...InteractiveDrawFragment
        ...InteractiveTableFragment
        ...InteractiveDiscussionFragment
        ...InteractiveInteractiveResponseFragment
        ...InteractiveInteractiveResponseCategoriseFragment
        ...InteractiveRiveAnimationFragment
        ...InteractiveGifFragment
      }
    }
  }
  ${InteractiveInfoBoxFragment}
  ${InteractiveResponseFragment}
  ${InteractiveVideoFragment}
  ${InteractiveAudioFragment}
  ${InteractiveImageFragment}
  ${InteractiveImageSphereFragment}
  ${InteractiveLinkFragment}
  ${InteractiveWebToolFragment}
  ${InteractiveGuidedResearchFragment}
  ${InteractiveEquationResponseFragment}
  ${InteractiveEbookFragment}
  ${InteractiveEbookInteractiveFragment}
  ${InteractiveInteractiveFragment}
  ${InteractiveSlideshowFragment}
  ${InteractiveDocumentFragment}
  ${InteractiveMultipleChoiceFragment}
  ${InteractiveActivityFragment}
  ${InteractiveVocabularyFragment}
  ${InteractiveFillInBlanksResponseFragment}
  ${InteractiveDrawFragment}
  ${InteractiveTableFragment}
  ${InteractiveDiscussionFragment}
  ${InteractiveInteractiveResponseSequenceFragment}
  ${InteractiveInteractiveResponseCategoriseFragment}
  ${InteractiveRiveAnimationFragment}
  ${InteractiveGifFragment}
`;

export const interactiveLessonSectionComponentFragment = gql`
  fragment InteractiveLessonSectionComponent on SectionComponent {
    ...InteractiveInfoBoxFragment
    ...InteractiveVideoFragment
    ...InteractiveAudioFragment
    ...InteractiveResponseFragment
    ...InteractiveImageFragment
    ...InteractiveImageSphereFragment
    ...InteractiveColumnsFragment
    ...InteractiveLinkFragment
    ...InteractiveWebToolFragment
    ...InteractiveGuidedResearchFragment
    ...InteractiveSlideshowFragment
    ...InteractiveEquationResponseFragment
    ...InteractiveDocumentFragment
    ...InteractiveEbookFragment
    ...InteractiveEbookInteractiveFragment
    ...InteractiveInteractiveFragment
    ...InteractiveMultipleChoiceFragment
    ...InteractiveActivityFragment
    ...InteractiveVocabularyFragment
    ...InteractiveFillInBlanksResponseFragment
    ...InteractiveDrawFragment
    ...InteractiveTableFragment
    ...InteractiveDiscussionFragment
    ...InteractiveInteractiveResponseFragment
    ...InteractiveInteractiveResponseCategoriseFragment
    ...InteractiveRiveAnimationFragment
    ...InteractiveGifFragment
  }
  ${ResponsiveImageFragment}
  ${InteractiveColumnsFragment}
  ${InteractiveInfoBoxFragment}
  ${InteractiveResponseFragment}
  ${InteractiveVideoFragment}
  ${InteractiveAudioFragment}
  ${InteractiveImageFragment}
  ${InteractiveImageSphereFragment}
  ${InteractiveLinkFragment}
  ${InteractiveWebToolFragment}
  ${InteractiveGuidedResearchFragment}
  ${InteractiveEbookFragment}
  ${InteractiveEbookInteractiveFragment}
  ${InteractiveInteractiveFragment}
  ${InteractiveSlideshowFragment}
  ${InteractiveEquationResponseFragment}
  ${InteractiveDocumentFragment}
  ${InteractiveMultipleChoiceFragment}
  ${InteractiveActivityFragment}
  ${InteractiveVocabularyFragment}
  ${InteractiveFillInBlanksResponseFragment}
  ${InteractiveDrawFragment}
  ${InteractiveTableFragment}
  ${InteractiveDiscussionFragment}
  ${InteractiveInteractiveResponseSequenceFragment}
  ${InteractiveInteractiveResponseCategoriseFragment}
  ${InteractiveRiveAnimationFragment}
  ${InteractiveGifFragment}
`;

export const interactiveLessonSubSectionFragment = gql`
  fragment InteractiveLessonSubSection on ActivitiesSection {
    id
    components {
      ...InteractiveLessonSectionComponent
    }
    sort
    parentSectionId
    pageType
    language
  }
  ${interactiveLessonSectionComponentFragment}
`;

export const interactiveLessonSectionFragment = gql`
  fragment InteractiveLessonSection on ActivitiesSection {
    id
    components {
      ...InteractiveLessonSectionComponent
    }
    subSections {
      ...InteractiveLessonSubSection
    }
    sort
    pageType
    language
  }
  ${interactiveLessonSectionComponentFragment}
  ${interactiveLessonSubSectionFragment}
`;

export const interactiveLessonSectionOverviewFragment = gql`
  fragment InteractiveLessonSectionOverview on ActivitiesSectionOverview {
    id
    differentiation
    checkPoint
    sort
    pageType
    subSectionsOverview {
      id
      differentiation
      checkPoint
      parentSectionId
      sort
      pageType
    }
  }
`;

export const interactiveLessonFragment = gql`
  fragment LessonActivities on LessonActivities {
    id
    language
    date
    focusQuestion {
      value
    }
    phenomena {
      value
    }
    landscapeImage {
      key
      fileName
      original
      alt
      data {
        flip
      }
      responsive {
        width
        height
        urls {
          url
          quality
        }
      }
    }
    landscapeImageSourceUrl
    styles {
      fontSize
      ...InteractiveContentStyleBackground
    }
    lesson {
      authorId
      bookmarked
      bookmarkMetaData {
        activitiesBookmarks {
          version
          sectionIds
        }
      }
      slug
      name
      years {
        name
      }
      unit {
        id
        name
      }
      subjects {
        id
        label
        colour
      }
      landscapeImage {
        responsive {
          width
          height
          urls {
            url
            quality
          }
        }
      }
    }
    sectionsOverview {
      ...InteractiveLessonSectionOverview
    }
    status
    version
    hasUnpublishedChanges
    downloadUrl
    printPdfKey
    printPdfKeyPublicToken
    studentPrintables {
      downloadUrl
      previewUrl
    }
    assetKeys {
      print {
        language
        key
        publicToken
      }
      studentPrintables {
        language
        key
        publicToken
      }
    }
    assetDownloadUrls {
      language
      url
    }
    availableTranslations
  }
  ${interactiveLessonSectionOverviewFragment}
  ${interactiveContentStyleBackgroundFragment}
`;

export const GET_DRAFT_LESSON_ACTIVITIES = gql`
  query GetDraftLessonActivities($input: GetLessonActivitiesInput!) {
    getDraftLessonActivities(input: $input) {
      ...LessonActivities
    }
  }
  ${interactiveLessonFragment}
`;

export const GET_ACTIVITIES_SECTIONS = gql`
  query GetActivitiesSections($input: GetActivitiesSectionsInput!) {
    getActivitiesSections(input: $input) {
      ...InteractiveLessonSection
    }
  }
  ${interactiveLessonSectionFragment}
`;

export const GET_ACTIVITIES_COMPONENT = gql`
  query GetActivitiesComponent($input: GetActivitiesComponentInput!) {
    getActivitiesComponent(input: $input) {
      ...InteractiveLessonSectionComponent
    }
  }
  ${interactiveLessonSectionComponentFragment}
`;

export const BUILD_DRAFT_LESSON_ACTIVITIES = gql`
  mutation BuildDraftLessonActivities(
    $input: BuildDraftLessonActivitiesInput!
  ) {
    buildDraftLessonActivities(input: $input) {
      ...LessonActivities
    }
  }
  ${interactiveLessonFragment}
`;

export const INSERT_ACTIVITIES_SECTION = gql`
  mutation InsertActivitiesSection($input: InsertActivitiesSectionInput!) {
    insertActivitiesSection(input: $input) {
      ...InteractiveLessonSection
    }
  }
  ${interactiveLessonSectionFragment}
`;

export const UPDATE_ACTIVITIES_SECTION = gql`
  mutation UpdateActivitiesSection($input: UpdateActivitiesSectionInput!) {
    updateActivitiesSection(input: $input) {
      ...InteractiveLessonSection
    }
  }
  ${interactiveLessonSectionFragment}
`;

export const UPDATE_ACTIVITIES_COMPONENT = gql`
  mutation UpdateActivitiesComponent($input: UpdateActivitiesComponentInput!) {
    updateActivitiesComponent(input: $input) {
      ...InteractiveLessonSectionComponent
    }
  }
  ${interactiveLessonSectionComponentFragment}
`;

export const DELETE_ACTIVITIES_COMPONENT = gql`
  mutation DeleteActivitiesComponent($input: DeleteActivitiesComponentInput!) {
    deleteActivitiesComponent(input: $input)
  }
`;

export const DELETE_ACTIVITIES_SECTION = gql`
  mutation DeleteActivitiesSection($input: DeleteActivitiesSectionInput!) {
    deleteActivitiesSection(input: $input)
  }
`;

export const GET_COMPONENT_TEMPLATES = gql`
  query GetComponentTemplates {
    getComponentTemplates {
      id
      data
      sort
      previews {
        format
        url
      }
      group
      name
      description
    }
  }
`;

export const USE_COMPONENT_TEMPLATE = gql`
  mutation UseComponentTemplate($input: UseComponentTemplateInput!) {
    useComponentTemplate(input: $input) {
      ...InteractiveLessonSectionComponent
    }
  }
  ${interactiveLessonSectionComponentFragment}
`;

export const UPDATE_LESSON_ACTIVITIES_FONT_SIZE = gql`
  mutation UpdateLessonActivitiesFontSize($input: UpdateFontSizeInput!) {
    updateLessonActivitiesFontSize(input: $input) {
      id
      language
      styles {
        fontSize
      }
    }
  }
`;

export const UPDATE_ACTIVITIES_FOCUS_QUESTION = gql`
  mutation UpdateActivitiesFocusQuestion(
    $input: UpdateLessonFocusQuestionInput!
  ) {
    updateActivitiesFocusQuestion(input: $input) {
      id
      language
      focusQuestion {
        value
      }
    }
  }
`;

export const UPDATE_ACTIVITIES_PHENOMENA = gql`
  mutation UpdateActivitiesPhenomena($input: UpdateLessonPhenomenaInput!) {
    updateActivitiesPhenomena(input: $input) {
      id
      language
      phenomena {
        value
      }
    }
  }
`;
